import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateCompany, UpdateCompany } from '@thro-types/jobmarket';

import { AxiosRequestConfig } from 'axios';

import {
  createCompany,
  deleteCompany,
  getCompanies,
  getCompany,
  getUniversityCompany,
  updateCompany,
  updateCompanyState
} from 'api/jobmarketService';
import { Company } from 'types/AppTypes';

/**
 * A custom hook for all companies from the Jobmarket Service.
 *
 * @returns UseQueryResult<Company[], Error>
 */
export const useJobmarketCompaniesQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['companies', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const companies = getCompanies(config);
      return companies;
    },
    placeholderData: keepPreviousData
  });

  return query;
};

export const useJobmarketUniversityCompanyQuery = (
  config?: AxiosRequestConfig,
  enabled?: boolean
) => {
  const query = useQuery({
    queryKey: ['companies', 'university', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const company = getUniversityCompany(config);
      return company;
    },
    enabled
  });

  return query;
};

export const useJobmarketCompanyQuery = (id?: number, config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['companies', id, config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      if (!id) {
        return;
      }
      const company = getCompany(id, config);
      return company;
    },
    enabled: !!id
  });

  return query;
};

export const useCompaniesMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey = ['companies'];
  const create = useMutation<Company, Error, CreateCompany>({
    mutationFn: async args => {
      return await createCompany(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['jobOfferings'] });
    },
    onError: data => {}
  });
  const update = useMutation<Company, Error, { id: number; data: UpdateCompany }>({
    mutationFn: async args => {
      return await updateCompany(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['jobOfferings'] });
    },
    onError: data => {}
  });

  const updateState = useMutation<
    Company,
    Error,
    { id: number; data: { state: string; reason?: string } }
  >({
    mutationFn: async args => {
      return await updateCompanyState(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['jobOfferings'] });
    },
    onError: data => {}
  });

  const deletion = useMutation<Company, Error, number>({
    mutationFn: async args => {
      return await deleteCompany(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['jobOfferings'] });
    },
    onError: data => {}
  });

  return { update, create, deletion, updateState } as const;
};
